import { useRouter } from '@moonpig/web-core-routing'
import { useStoreId } from '@moonpig/web-core-stores'
import { match, useIsMobile } from '@moonpig/web-shared-utils'
import { useExperiment } from '@moonpig/web-core-eppo/browser'
import { useProductsLocaleText } from '../../locale'
import { ProductInfoProduct } from '../../types'
import { isCardProduct } from '../../utils/isCardProduct'
import { Module } from '../types'
import { createGroupCardProjectDesktopConfiguration } from './group-project/GroupCardProjectDesktop'
import { createGroupCardProjectMobileConfiguration } from './group-project/GroupCardProjectMobile'
import { createCardMultiBuyDesktopConfiguration } from './multibuy/CardMultiBuyDesktop'
import { createCardMultiBuyMobileConfiguration } from './multibuy/CardMultiBuyMobile'
import { createCardOutOfStockDesktopConfiguration } from './out-of-stock/CardOutOfStockDesktop'
import { createCardOutOfStockMobileConfiguration } from './out-of-stock/CardOutOfStockMobile'
import { createCardFrictionlessFlowMobileConfiguration } from './frictionless-flow/CardFrictionlessFlowMobile'
import { createCardFrictionlessFlowDesktopConfiguration } from './frictionless-flow/CardFrictionlessFlowDesktop'
import { E_CARD_SIZE_ID } from '../../constants'
import { createCardFrictionlessFlowNoECardDesktopConfiguration } from './frictionless-flow/CardFrictionlessFlowNoECardDesktop'
import { createCardFrictionlessFlowNoECardMobileConfiguration } from './frictionless-flow/CardFrictionlessFlowNoECardMobile'

type UseDefaultModuleConfiguration = (context: {
  flags: Record<string, boolean>
}) => (input: { product: ProductInfoProduct }) => Module[]

export const useDefaultModuleConfiguration: UseDefaultModuleConfiguration =
  () => {
    const storeId = useStoreId()
    const localise = useProductsLocaleText()
    const isMobile = useIsMobile()
    const router = useRouter()
    const isFrictionlessFlowEnabled = useExperiment('frictionless-flow-v1', {
      fallback: false,
    })

    return ({ product }) => {
      const isGroupCardProject =
        !!router.getCurrentRoute<'search'>()?.params?.groupCardProject

      const conditions = {
        isCard: isCardProduct(product.category.department),
        inStock: product.variants.some(({ inStock }) => inStock),
        isMobile,
        isGroupCardProject,
        storeId,
        isFrictionlessFlowEnabled,
        hasECard: !!product.variants.find(({ key }) => key === E_CARD_SIZE_ID),
      }

      return match(conditions)
        .with(
          { isCard: false }, // prettier-ignore
          () => [], // prettier-ignore
        )
        .with(
          { inStock: false, isMobile: true }, // prettier-ignore
          () => createCardOutOfStockMobileConfiguration(), // prettier-ignore
        )
        .with(
          { inStock: false, isMobile: false }, // prettier-ignore
          () => createCardOutOfStockDesktopConfiguration(), // prettier-ignore
        )
        .with(
          { isGroupCardProject: true, isMobile: true,  }, // prettier-ignore
          () => createGroupCardProjectMobileConfiguration(), // prettier-ignore
        )
        .with(
          { isGroupCardProject: true, isMobile: false, }, // prettier-ignore
          () => createGroupCardProjectDesktopConfiguration(), // prettier-ignore
        )
        .with(
          { isMobile: true, isFrictionlessFlowEnabled: true, hasECard: true }, // prettier-ignore
          () => createCardFrictionlessFlowMobileConfiguration(), // prettier-ignore
        )
        .with(
          { isMobile: false, isFrictionlessFlowEnabled: true, hasECard: true }, // prettier-ignore
          () => createCardFrictionlessFlowDesktopConfiguration(), // prettier-ignore
        )
        .with(
          { isMobile: true, isFrictionlessFlowEnabled: true,  }, // prettier-ignore
          () => createCardFrictionlessFlowNoECardMobileConfiguration(), // prettier-ignore
        )
        .with(
          { isMobile: false, isFrictionlessFlowEnabled: true, }, // prettier-ignore
          () => createCardFrictionlessFlowNoECardDesktopConfiguration(), // prettier-ignore
        )
        .with(
          { isMobile: true }, // prettier-ignore
          () => createCardMultiBuyMobileConfiguration({ localise, storeId }), // prettier-ignore
        )
        .with(
          { isMobile: false }, // prettier-ignore
          () => createCardMultiBuyDesktopConfiguration({ localise, storeId }), // prettier-ignore
        )
        .otherwise(/* istanbul ignore next */ () => [])
    }
  }
